/**
 * Extracts components of a file path: directory path, filename (excluding extension),
 * and file extension. Handles paths with or without trailing slashes.
 *
 * Example:
 * Input: "path/to/foo.mp3"
 * Output: { path: "path/to/", filename: "foo", extension: ".mp3" }
 *
 * @param filePath - The file path to extract components from.
 * @returns An object containing:
 *   - `path`: The directory path, ending with a slash (if present) or empty if none.
 *   - `filename`: The file name without its extension.
 *   - `extension`: The file extension (including the dot) or `null` if none.
 *
 * @throws {Error} If the input file path is invalid.
 */
export function extractPathComponents(filePath: string): {
  path: string;
  filename: string;
  extension: string | null;
} {
  // Regex to match the directory path, filename, and file extension.
  const regex = /^(.*[/\\])?([^/\\]+?)(\.[^/\\.]*)?$/;

  /*
  Regex Explanation:
	•	^(.*[\/\\])?: Matches the directory path, ending with a slash or backslash (/ or \). It is optional due to the ? quantifier.
	•	([^\/\\]+?): Matches the filename without extension. This segment avoids slashes and captures the shortest possible match due to +?.
	•	(\.[^\/\\.]*)?: Matches the file extension, starting with a dot (.) and containing characters that are not slashes or dots. It is optional due to the ?.
   */
  const match = filePath.match(regex);

  if (!match) {
    throw new Error('Invalid file path');
  }

  const [, path = '', filename, extension = null] = match;
  return { path, filename, extension };
}
